<template>
  <div class="other-amount-title">
    <h3>{{ $t('superpacman-other-amount-title') }}</h3>
  </div>
  
  <div class="amount-to-be-paid-div">
    <div class="amount-to-be-paid-input-box">
      <input 
        v-model="amountToBePaid" 
        :placeholder="formattedAmount" 
        type="text" 
        inputmode="numeric"
        class="amount-to-be-paid-input"
        maxlength="14"
      >
    </div>
    <p class="minimum-amount-title">
      {{ $t('superpacman-minimum-amount') }}: {{ n(minimumAmount, "currency", locale) }}
    </p>
    <div class="other-amount-conditional-messages">
      <div v-if="amountJustRight">
        <p class="amount-just-right-text">
          <img 
            class="superpacman-other-amount-info-icon" 
            src="../assets/icons/info.svg"
          >
          {{ $t('superpacman-next-payment-date') }}: {{ $d(nextValidThrough, 'medium', locale) }}
        </p>
      </div>
      <div v-if="amountLowerThanMinError">
        <p class="superpacman-other-amount-warning-text">
          {{ $t('superpacman-minimum-amount-warning') }}
        </p>
      </div> 
      <div 
        v-if="amountHigherThanMaxError"
        class="superpacman-amount-higher-than-max"
      >
        <p 
          v-if="displayCreditLineMaxAmountMessage"
          class="superpacman-other-amount-warning-text"
        >
          {{ $t('superpacman-max-amount-exceeded-creditline', { creditLinePayoffAmount: n(Number(maximumAmount), 'currency', locale) }) }}
        </p>
        <p 
          v-else 
          class="superpacman-other-amount-warning-text"
        >
          {{ $t('superpacman-max-amount-exceeded-cumulative-balance', { fullPayoffAmount: n(Number(maximumAmount), 'currency', locale) }) }}
        </p>
      </div>
    </div>
    <button 
      :class="{'button-grey-bg-white-text': !amountJustRight, 'button-green-bg-white-text': amountJustRight}"
      v-on="enableClick ? {click: handleClick} : {}"
    >
      {{ $t('superpacman-confirm-button') }}
    </button>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, ref, } from "vue";
import {useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import { ISuperPacmanInstallment } from "@/types/ISuperPacman";
import { IInstallmentDetail } from "@/types/IInstallment";

export default defineComponent({
  name: "OtherAmountView",
  setup() {
    const {t, n, d, locale} = useI18n();
    const router = useRouter();
    const store = useStore();
    const superPacmanInstallment = computed(() => store.state.payOptions.superPacmanInstallment)
    const country = computed(() => superPacmanInstallment.value.financeProduct.country);
    const deviceTag = computed(
        () => store.state.payOptions.deviceInfo.deviceTag
    );
    let currencySymbol = computed(
        () => store.state.payOptions.deviceInfo.currencySymbol
    );
    const paymentOption = computed(
        () => store.state.payOptions.deviceInfo.paymentOption
    );
    const nextValidThrough = ref(Date.now());
    const enableClick = ref(false);
    const amountToBePaid = ref("");
    const amountLowerThanMinError = ref(false);
    const amountHigherThanMaxError = ref(false);
    const amountJustRight = ref(false);
    const formattedAmount = computed(() => {
      return n(0, "currency", locale.value)
    });
    const maximumAmount = computed(() => {
      return handleMaximumAmounts(superPacmanInstallment.value)
    });
    const minimumAmount = computed(() => {
      return Number(superPacmanInstallment.value.financeProduct.minimumPaymentAmount);
    })
    const displayCreditLineMaxAmountMessage = computed(() => {
      if (superPacmanInstallment.value.cumulativeBalance.enabled === true) {
        return false
      } else {
        return shouldDisplayCreditLineMaxAmountMessage(superPacmanInstallment.value)
      }
    });
    return {
      t,
      n,
      d,
      locale,
      store,
      router,
      country,
      deviceTag,
      currencySymbol,
      paymentOption,
      amountToBePaid,
      minimumAmount,
      maximumAmount,
      formattedAmount,
      amountLowerThanMinError,
      amountHigherThanMaxError,
      amountJustRight,
      nextValidThrough,
      superPacmanInstallment,
      enableClick,
      displayCreditLineMaxAmountMessage
    };
  },
  watch: {
    amountToBePaid(newValue) {
      /*
        This is a watcher for the v-model for variable amountToBePaid, which is the value in the input box.
        It validates and formats the amountToBePaid variable so that it only contains numbers, thousands commas, decimal points and a two digit decimal part.
      */

      // Assigning to a variable so that we don't alter newValue.
      let newValueToModify = newValue
      // Assigning a default number in case something goes wrong.
      let newValueNumber = 0

      /*
        This catches if the number has a decimal part. For example, you will enter this branch for values:
        0.
        0.0
        0.00
        And so
      */
      if (newValue.indexOf(".") >= 0) {
        // Get the index of the decimal point, to then get a substring of everything left of it (the whole or integer part) and everything right of it (the decimal part).
        let decimalPosition = newValue.indexOf(".")
        let integerPart = newValue.substring(0, decimalPosition)
        let decimalPart = newValue.substring(decimalPosition)
        // Removes anything that is not a number, a comma or a dot.
        integerPart = formatNumber(integerPart)
        decimalPart = formatNumber(decimalPart)
        // Ensures that the decimal part is only two digits
        decimalPart = decimalPart.substring(0,2)
        // Prepends the currency symbol and constructs the final displayable string with decimal points.
        newValueToModify = this.currencySymbol + integerPart + "." + decimalPart
        // Eliminates anything that is not a number or decimal dot in order to convert the value into a number.
        newValueNumber = Number(newValueToModify.replace(/[^0-9\.]/g, ""))
      } else {
        // If number does not contain a decimal dot, then it is a whole number.
        newValueToModify = formatNumber(newValue)
        newValueToModify  = this.currencySymbol + newValueToModify
        newValueNumber = Number(newValueToModify.replace(/[^0-9\.]/g, ""))
      }
      if (newValue === "") {return;}

      // Validate lower bound of payable amounts.
      if (newValueNumber < this.minimumAmount) {
        this.amountLowerThanMinError = true
      } else {
        this.amountLowerThanMinError = false
      }
      
      // Validate upper bound of payable amounts.
      if (newValueNumber > this.maximumAmount) {
        this.amountHigherThanMaxError = true
      } else {
        this.amountHigherThanMaxError = false
      }

      // Ensures that the inputted amount is greater or equal than the minimumAmount but less than or equal to the maximumAmount.
      if (newValueNumber >= this.minimumAmount && newValueNumber <= this.maximumAmount) {
        this.amountJustRight = true
        this.nextValidThrough = this.calculateNextValidThrough(newValueNumber, this.superPacmanInstallment)
        this.enableClick = true
      } else {
        this.amountJustRight = false
        this.enableClick = false
      }
      // Ensures that the displayed amount in the input box is the amount we cleaned and formatted
      this.amountToBePaid = newValueToModify
    }
  },
  methods: {
    calculateNextValidThrough(amount: number, superPacmanInstallment:  ISuperPacmanInstallment) {
      /*
        This function iterates through superPacmanInstallment.daysToAmount (which is a list that contains ISuperPacmanDaysToAmountItem objects). 

        If the amount given as a parameter is greater than the object's amount, it will grant the object's dateUnlockedUntil, which is a UNIX timestamp. 

        Since we know that the first entry in superPacmanInstallment.daysToAmount is the minimum payment and that the client must pay at least the minimum payment, we can assume that the amount parameter will always be greater than or equal to superPacmanInstallment.daysToAmount[0].
      */
      let lowestValidthroughIndex = 0;
      for (let i = 0; i < superPacmanInstallment.daysToAmount.length; i++) {
        if (amount >= Number(superPacmanInstallment.daysToAmount[i].amount)) {
          lowestValidthroughIndex = i
        }
      }
      // We multiply by 1000 because the Vue I18N library requires the UNIX timestamps to be multiplied by 1000 to display the correct date. Otherwise, it seems to use a far more distant date.
      const date = superPacmanInstallment.daysToAmount[lowestValidthroughIndex].dateUnlockedUntil * 1000
      return date
    },
    handleClick() {
      /*
        Since amountToBePaid is a string, we need to transform into a number, and we remove anything that is not a number or a dot.
        We already do a lot of the cleaning in the amountToBePaid watcher, so we know that by this point, amountToBePaid is going to follow the format: <currencySymbol><numbersWithCommas><decimalDot><decimalPart> (i.e. $100,000.00)

        We also know that amountToBePaid cannot be an empty string or an invalid number, since you cannot press the button if that is the case.
      */
      const amountToBePaidNumber = Number(this.amountToBePaid.replace(/[^0-9\.]/g, ""))
      const installmentToPay: IInstallmentDetail = {
        days: 0,
        amount: amountToBePaidNumber,
        term: " ",
        isOverPayment: false
      }
      this.store.dispatch("payOptions/setSelectedInstallment",  installmentToPay)
      this.store.dispatch("payOptions/setSuperPacmanAmount", amountToBePaidNumber)
      this.router.push({name: "ProvidersByDeviceTagAndCountry", params: {usingSP: "true"}});
    },
  }
});

const formatNumber = (numberString: string) => {
  /*
    Basically, remove anything that is not a number or a comma or a decimal point or a currency.
    Then, add thousands comma separator if it finds three digits. Regex was found online but seems to work.
  */
  return numberString.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

const handleMaximumAmounts = (superPacmanInstallment: ISuperPacmanInstallment) => {
  /*
    As per https://payjoy.atlassian.net/browse/VPOP-144, until cumulative balance is implemented, we will use the creditLinePayoffAmount as the max amount. 

    Once cumulative balance is implemented, use the function handleMaximumAmountsWithCumulativeBalance.

    We know this is safe as SP clients will always be JointLoanLine and if the creditLinePayoffAmount is 0, we will not display the other amount view.
  */
  let maximumAmount = 0;
  const creditLinePayoffAmount = superPacmanInstallment.financeProduct.creditLinePayoffAmount ? Number(superPacmanInstallment.financeProduct.creditLinePayoffAmount) : null;
  const foMinPaymentWithCbPlusCLPayoffAmount = superPacmanInstallment.financeProduct.foMinPaymentWithCbPlusCLPayoffAmount? Number(superPacmanInstallment.financeProduct.foMinPaymentWithCbPlusCLPayoffAmount) : null;
  const fullPayoffAmount = Number(superPacmanInstallment.financeProduct.fullPayoffAmount)
  /*
  This assumes that there can be a creditLinePayoffAmount without foMinPaymentWithCbPlusCLPayoffAmount.
  If that's the case, you must check first for the credit line payoff aount and use it. If it happens to be the case that you also have foMinPaymentWithCbPlusCLPayoffAmount, then use that instead.
  */
  if (creditLinePayoffAmount) {
    maximumAmount = creditLinePayoffAmount;
    if (foMinPaymentWithCbPlusCLPayoffAmount) {
      maximumAmount = foMinPaymentWithCbPlusCLPayoffAmount;
    }
  } 

  if (superPacmanInstallment.cumulativeBalance.enabled === true) {
    // These checks might not be necessary since we already validate the amount strings in Finance Engine SDK.
    if (fullPayoffAmount) {
      maximumAmount = fullPayoffAmount
    }
  }

  return maximumAmount
}

const shouldDisplayCreditLineMaxAmountMessage = (superPacmanInstallment: ISuperPacmanInstallment) => {
  /*
    As per https://payjoy.atlassian.net/browse/VPOP-144, until cumulative balance is implemented, we will use the creditLinePayoffAmount as the max amount. 

    Once cumulative balance is implemented, use the function shouldDisplayCreditLineMaxAmountMessageWithCumulativeBalance.

    We know this is safe as SP clients will always be JointLoanLine and if the creditLinePayoffAmount is 0, we will not display the other amount view.
  */
  let shouldDisplayCreditLineMaxAmountMessage = false;
  const creditLinePayoffAmount = superPacmanInstallment.financeProduct.creditLinePayoffAmount ? Number(superPacmanInstallment.financeProduct.creditLinePayoffAmount) : null;
  if (creditLinePayoffAmount) {
        shouldDisplayCreditLineMaxAmountMessage = true;
  }

  return shouldDisplayCreditLineMaxAmountMessage
}

</script>

<style scoped>

input {
  border: 0;
  font-size: xx-large;
}
input:focus {outline:none!important;}

.other-amount-title {
  width: 72vw;
  margin-left: 20px;
  margin-right: auto;
  height: auto;
}

.amount-to-be-paid-div {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
}

.amount-to-be-paid-input-box {
  margin-bottom: 10px;
}

.minimum-amount-title {
  font-size: smaller;
  color: #666666;
  margin-bottom: 0;
}

.amount-to-be-paid-input {
  width: auto;
  height: auto;
  text-align: center;
  font-family: "Inter";
  color: #666666;
  font-size: xxx-large;
}

.button-grey-bg-white-text {
  width: 92vw;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid #DDDDDD;
  background: #DDDDDD;
  margin-bottom: 40px;
  margin-left: 4vw;
  margin-right: auto;
  color: #FFFFFF;
  font-size: medium;
  font-weight: bold;
  margin-top: 58vh;
}

.button-green-bg-white-text {
  width: 92vw;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid #007C3B;
  background: #007C3B;
  margin-bottom: 40px;
  margin-left: 4vw;
  margin-right: auto;
  color: #FFFFFF;
  font-size: medium;
  font-weight: bold;
  margin-top: 58vh;
}

.other-amount-conditional-messages {
  width: auto;
  height: auto;
}

.amount-just-right-text {
  display: flex;
  width: 80vw;
  height: auto;
  border: 1px solid #D1D1D1;
  border-radius: 5px;
  max-width: 90vw;
  background: #F2F2F2;
  padding: 10px;
  font-size: 14px;
  font-weight: bold;
  margin: 0;
  margin-top: 15px;
  justify-content: center;
  color: #8B8B8B;
  padding-left: 15px;
  padding-right: 15px;
}

.superpacman-other-amount-info-icon {
  margin-right: 10px;
}

.superpacman-other-amount-warning-text {
  color: #C9372C;
  font-size: small;
  text-wrap: wrap;
  text-align: center;
  line-height: 17px;
}

.superpacman-amount-higher-than-max {
  max-width: 80vw;
}

@media screen and (max-height: 639px) {
  .button-grey-bg-white-text {
    margin-top: 19vh;
  }

  .button-green-bg-white-text {
    margin-top: 19vh;
  }
}

@media screen and (max-width: 320px) {
  .amount-to-be-paid-input {
    font-size: xx-large;
  }
}
</style>
